import { PipelineStatsPayload, State, StateProps, StateStats } from "./state";
import { ConfigJson, StateFilterValue } from "./filters";
import { Transition, TransitionProps } from "./transition";
import { DependentOperationManager } from "./dependent-operation";

export interface StatesMachineProps {
  id?: number;
  initState?: number;
  endState?: number;
  idMailing?: number;
  states: StateProps[];
  statusByState?: PipelineStatsPayload;
  finished?: boolean;
}

export const CHANNEL_OPERATIONS: Set<StateProps["operation"]> = new Set([
  "SMS",
  "WHATS",
  "ENVIA_ATENDIMENTO"
]);

export const stateMachineSequence: {
  label: string;
  operation: StateProps["operation"];
}[] = [
  { label: "Origem CSV", operation: "START" },
  { label: "Refin", operation: "REFIN" },
  { label: "Saque Complementar", operation: "SAQUE_COMPLEMENTAR" },
  { label: "Higienização Offline", operation: "HIGIENIZACAO_OFFLINE" },
  { label: "AnaliseAI", operation: "ANALISE_IA" },
  { label: "Filtro", operation: "FILTRO" },
  { label: "Digitação", operation: "DIGITACAO" },
  { label: "Telefonia", operation: "TELEFONIA" },
  { label: "SMS", operation: "SMS" },
  { label: "WhatsApp", operation: "WHATS" },
  { label: "Envio Atendimento", operation: "ENVIA_ATENDIMENTO" },
  { label: "Fim", operation: "END" }
];

const dependentOperationManager = new DependentOperationManager([
  {
    operation: "REFIN",
    baseStateType: "NOOP",
    dependentStateType: "EXECUTOR",
    entryEvent: "START"
  },
  {
    operation: "SAQUE_COMPLEMENTAR",
    baseStateType: "NOOP",
    dependentStateType: "EXECUTOR",
    entryEvent: "START"
  }
]);

interface TransitionRule {
  id: string;
  reason: string;
  isAllowed: (ctx: {
    source: State;
    target: State;
    machine: StatesMachine;
  }) => boolean;
}

export interface StateValidationError {
  stateId: number;
  operation: StateProps["operation"];
  field: string;
  message: string;
}

interface StateFieldValidationRule {
  field: StateValidationError["field"];
  message: string;
  isMissing?: (state: State) => boolean;
}

export class StatesMachine {
  id?: number;
  initState: StatesMachineProps["initState"];
  endState: StatesMachineProps["endState"];
  idMailing?: StatesMachineProps["idMailing"];
  states: State[];
  statusByState: Map<number, StateStats>;
  finished: boolean;
  private transitionRules: TransitionRule[] = [
    {
      id: "no-self-loop",
      reason: "Conexão não permitida",
      isAllowed: ({ source, target }) => source.id !== target.id
    },
    {
      id: "no-cycle",
      reason: "Conexão não permitida: criaria um ciclo",
      isAllowed: ({ source, target, machine }) =>
        !machine.wouldCreateCycle(source.id, target.id)
    },
    {
      id: "end-has-no-outgoing",
      reason: "O bloco Fim não pode ter conexões de saída",
      isAllowed: ({ source }) => source.operation !== "END"
    },
    {
      id: "start-has-no-incoming",
      reason: "O bloco Início não pode receber conexões",
      isAllowed: ({ target }) => target.operation !== "START"
    },
    {
      id: "refin-only-incoming-from-higienizacao-offline",
      reason:
        "O bloco Refin só pode receber conexões de saída do bloco Higienização Offline",
      isAllowed: ({ source, target }) =>
        target.operation !== "REFIN" ||
        source.operation === "HIGIENIZACAO_OFFLINE" ||
        source.operation === "REFIN"
    },
    {
      id: "channel-only-parallel-outgoing",
      reason:
        "Somente blocos de Canais (SMS, WhatsApp, Envio Atendimento) podem ser paralelizados",
      isAllowed: ({ source, target, machine }) => {
        const existingSuccessTargets = (source.transitions ?? [])
          .filter((t) => t.event === "SUCCESS")
          .map((t) => machine.getState(t.target))
          .filter(Boolean) as State[];

        if (!existingSuccessTargets.length) return true;

        const allTargetsAreChannels =
          existingSuccessTargets.every((t) =>
            CHANNEL_OPERATIONS.has(t.operation)
          ) && CHANNEL_OPERATIONS.has(target.operation);

        if (allTargetsAreChannels) return true;

        return existingSuccessTargets.some((t) => t.id === target.id);
      }
    },
    {
      id: "channel-single-incoming",
      reason:
        "Blocos de Canais não podem receber mais de uma conexão de entrada",
      isAllowed: ({ source, target, machine }) => {
        if (!CHANNEL_OPERATIONS.has(target.operation)) return true;

        const incomingSourceIds = machine.getIncomingSuccessSourceIds(
          target.id
        );
        if (!incomingSourceIds.size) return true;

        return incomingSourceIds.has(source.id);
      }
    },
    {
      id: "end-channel-only-multiple-incoming",
      reason:
        "Somente blocos de Canais (SMS, WhatsApp, Envio Atendimento) podem convergir para o Fim",
      isAllowed: ({ source, target, machine }) => {
        if (target.operation !== "END") return true;

        const incomingSourceIds = machine.getIncomingSuccessSourceIds(
          target.id
        );
        if (!incomingSourceIds.size) return true;

        if (incomingSourceIds.size === 1 && incomingSourceIds.has(source.id)) {
          return true;
        }

        if (CHANNEL_OPERATIONS.has(source.operation)) {
          return [...incomingSourceIds].every((id) => {
            const s = machine.getState(id);
            return s && CHANNEL_OPERATIONS.has(s.operation);
          });
        }

        return false;
      }
    }
  ];
  private stateValidationRulesByOperation: Partial<
    Record<StateProps["operation"], StateFieldValidationRule[]>
  > = {
    REFIN: [
      { field: "banco", message: "O banco é obrigatório para Refin" }
      // { field: "convenio", message: "O convênio é obrigatório para Refin" },
      // {
      //   field: "codigoTabela",
      //   message: "O código da tabela é obrigatório para Refin"
      // }
    ],
    SAQUE_COMPLEMENTAR: [
      {
        field: "banco",
        message: "O banco é obrigatório para Saque Complementar"
      },
      {
        field: "idConvenio",
        message: "O convênio é obrigatório para Saque Complementar"
      },
      {
        field: "tipoCartao",
        message: "O tipo de cartão é obrigatório para Saque Complementar"
      },
      {
        field: "codigoTabela",
        message: "O código da tabela é obrigatório para Saque Complementar"
      }
    ],
    DIGITACAO: [
      { field: "banco", message: "O banco é obrigatório para Digitação" },
      {
        field: "convenio",
        message: "O convênio é obrigatório para Digitação"
      },
      { field: "tabela", message: "A tabela é obrigatória para Digitação" },
      {
        field: "usuarioDigitacao",
        message: "O usuário de digitação é obrigatório para Digitação"
      },
      {
        field: "usuarioDigitacaoSenha",
        message: "A senha do usuário de digitação é obrigatória para Digitação"
      },
      {
        field: "cpfUsuarioCertificado",
        message: "O CPF do usuário certificado é obrigatório para Digitação"
      },
      {
        field: "tipoColunaMargem",
        message: "O tipo de coluna margem é obrigatório para Digitação"
      },
      { field: "operacao", message: "A operação é obrigatória para Digitação" }
    ],
    SMS: [
      { field: "nome", message: "O nome é obrigatório para SMS" },
      { field: "mensagem", message: "A mensagem é obrigatória para SMS" },
      {
        field: "quantidadeMaximaTelLead",
        message:
          "A quantidade máxima de telefone por lead é obrigatória para SMS",
        isMissing: (state) => !state.quantidadeMaximaTelLead
      },
      {
        field: "mappedFields",
        message: "Os campos mapeados são obrigatórios para SMS",
        isMissing: (state) => !state.mappedFields
      }
    ],
    WHATS: [
      { field: "name", message: "O name é obrigatório para WhatsApp" },
      {
        field: "qtdEstimadaEnvioDia",
        message:
          "A quantidade estimada de envio por dia é obrigatória para WhatsApp",
        isMissing: (state) => !state.qtdEstimadaEnvioDia
      },
      { field: "type", message: "O tipo é obrigatório para WhatsApp" },
      {
        field: "mensagemNormal",
        message: "A mensagem é obrigatória para WhatsApp",
        isMissing: (state) => state.type === "TEXT" && !state.mensagemNormal
      },
      {
        field: "mensagemSaudacao",
        message:
          "A mensagem de saudação é obrigatória quando habilitada para WhatsApp",
        isMissing: (state) =>
          !!state.enabledMensagemSaudacao && !state.mensagemSaudacao
      },
      {
        field: "wppSessionIds",
        message: "Os participantes do WhatsApp são obrigatórios",
        isMissing: (state) =>
          !state.wppSessionIds || state.wppSessionIds.length === 0
      }
    ],
    FILTRO: [
      {
        field: "configJson",
        message: "É necessário configurar pelo menos 1 filtro",
        isMissing: (state) => {
          const formatted = state.configJson?.formatToSend();
          return !formatted || Object.keys(formatted).length === 0;
        }
      }
    ]
  };

  constructor({
    id,
    endState = 1,
    initState = 1,
    idMailing,
    states,
    finished,
    statusByState
  }: StatesMachineProps) {
    this.id = id;
    this.endState = endState;
    this.initState = initState;
    this.idMailing = idMailing;
    this.states = states.map((state) => new State(state));
    this.finished = finished ?? statusByState?.pipelineFinished ?? false;
    const stateEntries = Object.entries(statusByState?.states ?? {});
    const parsedEntries = stateEntries.map(
      ([key, value]) => [Number(key), value] as [number, StateStats]
    );
    this.statusByState = new Map(parsedEntries);

    this.syncTransitionsBySequence();
  }

  public addState(state: State) {
    this.states.push(state);
    this.syncTransitionsBySequence();
  }

  public removeState(stateId: number) {
    const stateToRemove = this.getState(stateId);
    if (!stateToRemove) return;
    if (["START", "END"].includes(stateToRemove.operation)) return;

    this.states = this.states.filter((state) => state.id !== stateId);
    for (const state of this.states) {
      if (!state.transitions) continue;
      const filteredTransitions = state.transitions.filter(
        (transition) => transition.target !== stateId
      );
      state.transitions = filteredTransitions.length
        ? filteredTransitions
        : undefined;
    }

    this.syncTransitionsBySequence();
  }

  public getState(id: number) {
    return this.states.find((state) => state.id === id);
  }

  public getVisibleStateId(stateId: number) {
    const state = this.getState(stateId);
    if (!state) return stateId;

    const rule = dependentOperationManager.getRule(state.operation);
    if (!rule) return state.id;

    if (state.stateType === rule.dependentStateType) {
      const baseState = dependentOperationManager.getBaseState(
        state.operation,
        this.states
      );
      return baseState?.id ?? state.id;
    }

    return state.id;
  }

  public getVisibleStates() {
    return this.states.filter((state) => {
      const rule = dependentOperationManager.getRule(state.operation);
      if (!rule) return true;

      if (state.stateType === rule.baseStateType) return true;

      const baseState = dependentOperationManager.getBaseState(
        state.operation,
        this.states
      );
      return !baseState;
    });
  }

  public canConnectStates(sourceId: number, targetId: number) {
    return !this.getConnectionBlockedReason(sourceId, targetId);
  }

  public getConnectionBlockedReason(
    sourceId: number,
    targetId: number
  ): string | null {
    const sourceStates = this.getOutgoingConnectionStates(sourceId);
    const targetStates = this.getIncomingConnectionStates(targetId);

    if (!sourceStates.length || !targetStates.length) return "Conexão inválida";

    for (const sourceState of sourceStates) {
      for (const targetState of targetStates) {
        const blockedRule = this.getBlockingRule(sourceState, targetState);
        if (blockedRule) {
          return blockedRule.reason;
        }
      }
    }

    return null;
  }

  public connectStates(
    sourceId: number,
    targetId: number,
    event?: TransitionProps["event"]
  ) {
    const sourceStates = this.getOutgoingConnectionStates(sourceId);
    const targetStates = this.getIncomingConnectionStates(targetId);

    if (!sourceStates.length || !targetStates.length) return;

    for (const sourceState of sourceStates) {
      for (const targetState of targetStates) {
        const transitionEvent =
          event ?? this.resolveTransitionEvent(sourceState, targetState);

        if (!this.isTransitionAllowed(sourceState, targetState)) {
          continue;
        }

        const stateTransitions = sourceState.transitions ?? [];
        const hasTransition = stateTransitions.some(
          (transition) =>
            transition.target === targetState.id &&
            transition.event === transitionEvent
        );
        if (hasTransition) continue;

        stateTransitions.push(
          new Transition({
            event: transitionEvent,
            target: targetState.id
          })
        );
        sourceState.transitions = stateTransitions;
      }
    }

    this.syncTransitionsBySequence();
  }

  public disconnectStates(sourceId: number, targetId: number) {
    const sourceStates = this.getOutgoingConnectionStates(sourceId);
    const targetStates = this.getIncomingConnectionStates(targetId);

    if (!sourceStates.length || !targetStates.length) return;
    const targetIds = targetStates.map((state) => state.id);

    for (const sourceState of sourceStates) {
      if (!sourceState.transitions) continue;

      const filteredTransitions = sourceState.transitions.filter(
        (transition) => !targetIds.includes(transition.target)
      );

      sourceState.transitions = filteredTransitions.length
        ? filteredTransitions
        : undefined;
    }

    this.syncTransitionsBySequence();
  }

  public getNextStateId() {
    if (!this.states.length) return 1;
    return Math.max(...this.states.map((state) => state.id)) + 1;
  }

  public static getNextStateIdFrom(states: State[]) {
    if (!states.length) return 1;
    return Math.max(...states.map((state) => state.id)) + 1;
  }

  public getExecutorState(operation: StateProps["operation"]) {
    return (
      this.states.find(
        (state) =>
          state.operation === operation && state.stateType === "EXECUTOR"
      ) ?? this.states.find((state) => state.operation === operation)
    );
  }

  public toggleOperation(
    operation: StateProps["operation"],
    isChecked: boolean
  ) {
    if (["START", "END"].includes(operation)) return;

    const exists = this.states.find((state) => state.operation === operation);
    if (isChecked) {
      if (!exists) {
        this.addOperationState(operation);
      }
    } else if (exists) {
      const statesToRemove = this.states
        .filter((state) => state.operation === operation)
        .map((state) => state.id);

      this.states = this.states.filter(
        (state) => state.operation !== operation
      );
      for (const state of this.states) {
        if (!state.transitions) continue;
        const filteredTransitions = state.transitions.filter(
          (transition) => !statesToRemove.includes(transition.target)
        );
        state.transitions = filteredTransitions.length
          ? filteredTransitions
          : undefined;
      }
      this.syncTransitionsBySequence();
    }
  }

  public addOperationState(operation: StateProps["operation"]) {
    const existingState = this.states.find(
      (state) => state.operation === operation
    );
    if (existingState) return existingState;

    const stateType = dependentOperationManager.getDefaultStateType(operation);
    const newState = new State({
      id: this.getNextStateId(),
      operation,
      stateType
    });

    this.addState(newState);
    return newState;
  }

  public removeOperation(operation: StateProps["operation"]) {
    if (["START", "END"].includes(operation)) return;

    const statesToRemove = this.states
      .filter((state) => state.operation === operation)
      .map((state) => state.id);
    if (!statesToRemove.length) return;

    this.states = this.states.filter((state) => state.operation !== operation);
    for (const state of this.states) {
      if (!state.transitions) continue;
      const filteredTransitions = state.transitions.filter(
        (transition) => !statesToRemove.includes(transition.target)
      );
      state.transitions = filteredTransitions.length
        ? filteredTransitions
        : undefined;
    }

    this.syncTransitionsBySequence();
  }

  public updateOperationState(
    operation: StateProps["operation"],
    updater: (state: State) => void
  ) {
    const state = this.getExecutorState(operation);
    if (!state) return;
    updater(state);
  }

  public updateOperationConfig(
    operation: StateProps["operation"],
    updater: (config: Record<string, StateFilterValue>) => void
  ) {
    let state = this.getExecutorState(operation);
    if (!state) {
      state = new State({
        id: this.getNextStateId(),
        operation,
        stateType: "EXECUTOR"
      });
      this.addState(state);
    }
    const currentConfig = state.configJson?.formatToSend() || {};
    updater(currentConfig);
    state.configJson = new ConfigJson(currentConfig);
  }

  public getFilterState() {
    return this.states.find((state) => state.operation === "FILTRO");
  }

  public isFilterSelected(key: string) {
    return !!this.getFilterState()?.configJson?.getFilter(key);
  }

  public toggleFilterConfig(
    key: string,
    isChecked: boolean,
    filterDefaults: Record<string, StateFilterValue>
  ) {
    const filtroState = this.getFilterState();
    const currentConfig = filtroState?.configJson?.formatToSend() || {};

    if (isChecked) {
      currentConfig[key] = filterDefaults[key] ?? (true as StateFilterValue);
    } else {
      delete currentConfig[key];
    }

    const hasFilters = Object.keys(currentConfig).length > 0;

    if (!filtroState && hasFilters) {
      const newState = new State({
        id: this.getNextStateId(),
        operation: "FILTRO",
        stateType: "EXECUTOR",
        configJson: currentConfig
      });
      this.addState(newState);
      return;
    }

    if (filtroState) {
      if (hasFilters) {
        filtroState.configJson = new ConfigJson(currentConfig);
      } else {
        this.states = this.states.filter((state) => state !== filtroState);
      }
    }
  }

  public getFilterValue(key: string): StateFilterValue | undefined {
    return this.getFilterState()?.configJson?.getFilterValue(key);
  }

  public getFilterRangeValue(
    key: string
  ):
    | { gte: string | number | undefined; lte: string | number | undefined }
    | undefined {
    return this.getFilterState()?.configJson?.getRangeValue(key);
  }

  public getFilterMultiValue(key: string): any[] {
    return this.getFilterState()?.configJson?.getMultiValue(key) ?? [];
  }

  public setFilterRangeValue(
    key: string,
    side: "gte" | "lte",
    value: string,
    isDate?: boolean
  ) {
    const state = this.getOrCreateFilterState();
    if (!state.configJson) state.configJson = new ConfigJson({});
    state.configJson.setRangeValue(key, side, value, isDate);
  }

  public setFilterSimpleValue(key: string, value: StateFilterValue) {
    const state = this.getOrCreateFilterState();
    if (!state.configJson) state.configJson = new ConfigJson({});
    state.configJson.setValue(key, value);
  }

  public setFilterMultiValue(key: string, value: any) {
    const state = this.getOrCreateFilterState();
    if (!state.configJson) state.configJson = new ConfigJson({});

    if (Array.isArray(value)) {
      state.configJson.setValue(key, value);
      return;
    }

    const isInclude = state.configJson
      .getMultiValue(key)
      .find((option) => option === value);
    const newValue = isInclude
      ? state.configJson.getMultiValue(key).filter((option) => option !== value)
      : [...state.configJson.getMultiValue(key), value];

    state.configJson.setValue(key, newValue);
  }

  public isInvalidPipeline(): boolean {
    const coreStates = this.states.filter(
      (state) => !["START", "END"].includes(state.operation)
    );

    // O pipeline precisa ter ao menos um state de negócio
    if (!coreStates.length) {
      return true;
    }

    // Verificar se há pelo menos um state EXECUTOR de negócio
    const hasAnyExecutor = coreStates.some(
      (state) => state.stateType === "EXECUTOR"
    );
    if (!hasAnyExecutor) {
      return true;
    }

    // Verificar cada state NOOP
    const noopStates = coreStates.filter((state) => state.stateType === "NOOP");

    for (const noopState of noopStates) {
      // Verificar se existe um EXECUTOR correspondente
      const hasCorrespondingExecutor = coreStates.some(
        (state) =>
          state.operation === noopState.operation &&
          state.stateType === "EXECUTOR"
      );

      if (!hasCorrespondingExecutor) {
        return true;
      }

      // Lógica especial para REFIN: se há REFIN NOOP, precisa ter REFIN EXECUTOR
      if (noopState.operation === "REFIN") {
        const refinExecutorStates = coreStates.filter(
          (state) =>
            state.operation === "REFIN" && state.stateType === "EXECUTOR"
        );

        // Se há REFIN NOOP, precisamos ter REFIN EXECUTOR states correspondentes
        if (refinExecutorStates.length === 0) {
          return true;
        }
      }

      // Lógica especial para SAQUE_COMPLEMENTAR: se há SAQUE NOOP, precisa ter SAQUE EXECUTOR
      if (noopState.operation === "SAQUE_COMPLEMENTAR") {
        const saqueExecutorStates = coreStates.filter(
          (state) =>
            state.operation === "SAQUE_COMPLEMENTAR" &&
            state.stateType === "EXECUTOR"
        );

        // Se há SAQUE_COMPLEMENTAR NOOP, precisamos ter SAQUE_COMPLEMENTAR EXECUTOR states
        if (saqueExecutorStates.length === 0) {
          return true;
        }
      }
    }

    if (this.hasDisconnectedStatesInFlow()) {
      return true;
    }

    return false;
  }

  private hasDisconnectedStatesInFlow() {
    const startState = this.states.find((state) => state.operation === "START");
    const endState = this.states.find((state) => state.operation === "END");

    if (!startState || !endState) {
      return true;
    }

    const adjacency = new Map<number, number[]>();
    const reverseAdjacency = new Map<number, number[]>();

    for (const state of this.states) {
      const targets = (state.transitions ?? [])
        .filter((transition) => transition.event !== "ERROR")
        .map((transition) => transition.target);

      adjacency.set(state.id, targets);

      if (!reverseAdjacency.has(state.id)) {
        reverseAdjacency.set(state.id, []);
      }

      for (const targetId of targets) {
        const incoming = reverseAdjacency.get(targetId) ?? [];
        incoming.push(state.id);
        reverseAdjacency.set(targetId, incoming);
      }
    }

    const reachableFromStart = this.collectReachableStateIds(
      startState.id,
      adjacency
    );
    const canReachEnd = this.collectReachableStateIds(
      endState.id,
      reverseAdjacency
    );

    return this.states.some((state) => {
      if (["START", "END"].includes(state.operation)) {
        return false;
      }

      return !reachableFromStart.has(state.id) || !canReachEnd.has(state.id);
    });
  }

  private collectReachableStateIds(
    originId: number,
    graph: Map<number, number[]>
  ) {
    const visited = new Set<number>();
    const stack = [originId];

    while (stack.length) {
      const currentId = stack.pop();
      if (currentId === undefined || visited.has(currentId)) continue;

      visited.add(currentId);

      const neighbors = graph.get(currentId) ?? [];
      for (const neighborId of neighbors) {
        if (!visited.has(neighborId)) {
          stack.push(neighborId);
        }
      }
    }

    return visited;
  }

  private getStateValidationErrors(state: State): StateValidationError[] {
    const rules = this.stateValidationRulesByOperation[state.operation] ?? [];

    return rules
      .filter((rule) => {
        if (rule.isMissing) {
          return rule.isMissing(state);
        }

        return !state[rule.field as keyof State];
      })
      .map((rule) => ({
        stateId: state.id,
        operation: state.operation,
        field: rule.field,
        message: rule.message
      }));
  }

  public checkStateFields(): StateValidationError[] {
    return this.states
      .filter((state) => state.stateType === "EXECUTOR")
      .flatMap((state) => this.getStateValidationErrors(state));
  }

  public getPendingConfigCountByVisibleState(): Map<number, number> {
    const pendingConfigCountByVisibleState = new Map<number, number>();

    for (const error of this.checkStateFields()) {
      const visibleStateId = this.getVisibleStateId(error.stateId);
      pendingConfigCountByVisibleState.set(
        visibleStateId,
        (pendingConfigCountByVisibleState.get(visibleStateId) ?? 0) + 1
      );
    }

    for (const visibleState of this.getVisibleStates()) {
      const validationRules =
        this.stateValidationRulesByOperation[visibleState.operation] ?? [];

      if (!validationRules.length) continue;

      const hasExecutorState = this.states.some(
        (state) =>
          state.operation === visibleState.operation &&
          state.stateType === "EXECUTOR"
      );

      if (hasExecutorState) continue;

      const currentCount =
        pendingConfigCountByVisibleState.get(visibleState.id) ?? 0;

      // Se a operação ainda não possui EXECUTOR, considerar os campos
      // obrigatórios como pendentes para destacar o nó no fluxograma.
      pendingConfigCountByVisibleState.set(
        visibleState.id,
        currentCount + validationRules.length
      );
    }

    return pendingConfigCountByVisibleState;
  }

  public formatToSend() {
    const body = {
      id: this.id,
      initState: this.initState,
      endState: this.endState,
      idMailing: this.idMailing,
      finished: this.finished,
      statusByState: {
        id: this.id ?? 0,
        pipelineFinished: this.finished,
        states: Object.fromEntries(this.statusByState)
      },
      states: this.states.map((state) => state.formatToSend())
    };
    return JSON.parse(JSON.stringify(body));
  }

  public updateStats(payload: PipelineStatsPayload) {
    if (!payload || !payload.states || typeof payload.states !== "object")
      return;
    if (typeof payload.pipelineFinished === "boolean") {
      this.finished = payload.pipelineFinished;
    }
    for (const [stateIdStr, stats] of Object.entries(payload.states)) {
      this.statusByState.set(Number(stateIdStr), stats);
    }
  }

  public clone() {
    const formatted = this.formatToSend();
    const cloned = new StatesMachine(formatted);
    return cloned;
  }

  private getOrCreateFilterState() {
    let state = this.getFilterState();
    if (!state) {
      state = new State({
        id: this.getNextStateId(),
        operation: "FILTRO",
        stateType: "EXECUTOR"
      });
      this.addState(state);
    }
    return state;
  }

  public syncTransitionsBySequence() {
    this.ensureBoundaryStates();
    this.normalizeDependentOperationStates();
    this.syncDependentOperationTransitions();
    this.sanitizeTransitions();
    this.ensureErrorTransitionsToEnd();
    this.sortBoundaryStates();

    const startState = this.states.find((state) => state.operation === "START");
    const endState = this.states.find((state) => state.operation === "END");

    if (startState) this.initState = startState.id;
    if (endState) this.endState = endState.id;
  }

  private ensureBoundaryStates() {
    let startState = this.states.find((state) => state.operation === "START");
    if (!startState) {
      startState = new State({
        id: this.getNextStateId(),
        operation: "START",
        stateType: "NOOP"
      });
      this.states.push(startState);
    }

    let endState = this.states.find((state) => state.operation === "END");
    if (!endState) {
      endState = new State({
        id: this.getNextStateId(),
        operation: "END",
        stateType: "EXECUTOR"
      });
      this.states.push(endState);
    }

    startState.stateType = "NOOP";
    endState.stateType = "EXECUTOR";
  }

  private normalizeDependentOperationStates() {
    for (const { operation } of stateMachineSequence) {
      const rule = dependentOperationManager.getRule(operation);
      if (!rule) continue;

      const statesByOperation = this.states.filter(
        (state) => state.operation === operation
      );
      if (!statesByOperation.length) continue;

      let baseState = statesByOperation.find(
        (state) => state.stateType === rule.baseStateType
      );

      if (!baseState) {
        baseState = new State({
          id: this.getNextStateId(),
          operation,
          stateType: rule.baseStateType
        });
        this.states.push(baseState);
      }

      for (const state of statesByOperation) {
        if (state.id === baseState.id) continue;
        state.stateType = rule.dependentStateType;
      }
    }
  }

  private syncDependentOperationTransitions() {
    for (const { operation } of stateMachineSequence) {
      const baseState = dependentOperationManager.getBaseState(
        operation,
        this.states
      );
      if (!baseState) continue;

      const dependentStates = dependentOperationManager
        .getDependentStates(operation, this.states)
        .sort((a, b) => a.id - b.id);
      const rule = dependentOperationManager.getRule(operation);
      if (!rule) continue;

      if (!dependentStates.length) {
        baseState.transitions = undefined;
        continue;
      }

      baseState.transitions = dependentStates.map(
        (state) =>
          new Transition({
            event: rule.entryEvent,
            target: state.id
          })
      );
    }
  }

  private ensureErrorTransitionsToEnd() {
    const endState = this.states.find((state) => state.operation === "END");
    if (!endState) return;

    for (const state of this.states) {
      if (state.stateType !== "EXECUTOR") continue;
      if (state.operation === "END") continue;

      const transitionsWithoutError = (state.transitions ?? []).filter(
        (transition) => transition.event !== "ERROR"
      );

      state.transitions = [
        ...transitionsWithoutError,
        new Transition({
          event: "ERROR",
          target: endState.id
        })
      ];
    }
  }

  private sanitizeTransitions() {
    const stateIds = new Set(this.states.map((state) => state.id));
    const endState = this.states.find((state) => state.operation === "END");
    const successToEndSourceIds = new Set<number>();

    for (const state of this.states) {
      if (!state.transitions?.length) {
        state.transitions = undefined;
        continue;
      }

      const uniqueTransitions = new Map<string, Transition>();

      for (const transition of state.transitions) {
        if (!stateIds.has(transition.target)) continue;

        const targetState = this.getState(transition.target);
        if (!targetState) continue;
        if (!this.isTransitionAllowed(state, targetState)) continue;

        // Enforce: multiple SUCCESS→END only from channel sources
        if (
          endState &&
          transition.event === "SUCCESS" &&
          transition.target === endState.id
        ) {
          const isChannelSource = CHANNEL_OPERATIONS.has(state.operation);
          const allExistingAreChannels = [...successToEndSourceIds].every(
            (id) => {
              const s = this.getState(id);
              return s && CHANNEL_OPERATIONS.has(s.operation);
            }
          );

          if (isChannelSource && allExistingAreChannels) {
            successToEndSourceIds.add(state.id);
          } else if (!successToEndSourceIds.size) {
            successToEndSourceIds.add(state.id);
          } else if (!successToEndSourceIds.has(state.id)) {
            continue;
          }
        }

        const key = `${transition.event}:${transition.target}`;
        if (!uniqueTransitions.has(key)) {
          uniqueTransitions.set(
            key,
            new Transition({
              event: transition.event,
              target: transition.target
            })
          );
        }
      }

      // Enforce: multiple outgoing SUCCESS only when all targets are channels
      const successTransitions = [...uniqueTransitions.values()].filter(
        (t) => t.event === "SUCCESS"
      );
      if (successTransitions.length > 1) {
        const allTargetsAreChannels = successTransitions.every((t) => {
          const target = this.getState(t.target);
          return target && CHANNEL_OPERATIONS.has(target.operation);
        });

        if (!allTargetsAreChannels) {
          const firstSuccess = successTransitions[0];
          for (const t of successTransitions.slice(1)) {
            uniqueTransitions.delete(`${t.event}:${t.target}`);
          }
          uniqueTransitions.set(
            `${firstSuccess.event}:${firstSuccess.target}`,
            firstSuccess
          );
        }
      }

      const nextTransitions = [...uniqueTransitions.values()];
      state.transitions = nextTransitions.length ? nextTransitions : undefined;
    }
  }

  private sortBoundaryStates() {
    const startState = this.states.find((state) => state.operation === "START");
    const endState = this.states.find((state) => state.operation === "END");
    const middleStates = this.states
      .filter((state) => !["START", "END"].includes(state.operation))
      .sort((a, b) => a.id - b.id);

    this.states = [
      ...(startState ? [startState] : []),
      ...middleStates,
      ...(endState ? [endState] : [])
    ];
  }

  private getOutgoingConnectionStates(stateId: number) {
    const state = this.getState(stateId);
    if (!state) return [];

    const rule = dependentOperationManager.getRule(state.operation);
    if (!rule || state.stateType !== rule.baseStateType) {
      return [state];
    }

    const dependentStates = dependentOperationManager.getDependentStates(
      state.operation,
      this.states
    );

    return dependentStates.length ? dependentStates : [state];
  }

  private getIncomingConnectionStates(stateId: number) {
    const state = this.getState(stateId);
    if (!state) return [];

    const rule = dependentOperationManager.getRule(state.operation);
    if (!rule || state.stateType === rule.baseStateType) {
      return [state];
    }

    const baseState = dependentOperationManager.getBaseState(
      state.operation,
      this.states
    );

    return baseState ? [baseState] : [state];
  }

  private isTransitionAllowed(source: State, target: State) {
    return !this.getBlockingRule(source, target);
  }

  private getBlockingRule(
    source: State,
    target: State
  ): TransitionRule | undefined {
    return this.transitionRules.find(
      (rule) => !rule.isAllowed({ source, target, machine: this })
    );
  }

  public getIncomingSuccessSourceIds(stateId: number): Set<number> {
    const ids = new Set<number>();
    for (const state of this.states) {
      if (!state.transitions?.length) continue;
      if (
        state.transitions.some(
          (t) => t.target === stateId && t.event === "SUCCESS"
        )
      ) {
        ids.add(state.id);
      }
    }
    return ids;
  }

  private resolveTransitionEvent(source: State, target: State) {
    const rule = dependentOperationManager.getRule(source.operation);
    if (rule && source.stateType === rule.baseStateType) {
      return rule.entryEvent;
    }

    return "SUCCESS" as TransitionProps["event"];
  }

  private wouldCreateCycle(sourceId: number, targetId: number) {
    if (sourceId === targetId) return true;
    return this.hasPath(targetId, sourceId);
  }

  private hasPath(fromId: number, toId: number) {
    if (fromId === toId) return true;

    const visited = new Set<number>();
    const stack = [fromId];

    while (stack.length) {
      const currentId = stack.pop();
      if (currentId === undefined) continue;
      if (visited.has(currentId)) continue;

      visited.add(currentId);
      const currentState = this.getState(currentId);
      if (!currentState?.transitions?.length) continue;

      for (const transition of currentState.transitions) {
        if (transition.target === toId) return true;
        if (!visited.has(transition.target)) {
          stack.push(transition.target);
        }
      }
    }

    return false;
  }
}
